// Real API service for AppUnderTest
// Connects to the Express BFF backend

const API_BASE = '/api';
const TOKEN_KEY = 'appundertest_token';

// Get auth token from localStorage
const getToken = () => localStorage.getItem(TOKEN_KEY);

// Set auth token in localStorage
const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);

// Remove auth token from localStorage
const removeToken = () => localStorage.removeItem(TOKEN_KEY);

// Get auth header if token exists
const getAuthHeader = () => {
  const token = getToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Generic API call function
const apiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE}${endpoint}`;

  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
      ...options.headers
    }
  };

  console.log(`[API] ${options.method || 'GET'} ${url}`);

  const response = await fetch(url, config);

  const data = await response.json();

  // Handle 401 - but only treat as session expired if we had a token
  if (response.status === 401 && getToken()) {
    removeToken();
    const error = new Error('Session expired');
    error.status = 401;
    throw error;
  }

  if (!response.ok) {
    const error = new Error(data.error || 'API request failed');
    error.status = response.status;
    throw error;
  }

  return data;
};

// API Methods
export const apiService = {
  // Token management
  getToken,
  setToken,
  removeToken,

  // Authentication
  async login(email, password) {
    const result = await apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });

    if (result.token) {
      setToken(result.token);
    }

    return result;
  },

  async register(userData) {
    const result = await apiCall('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData)
    });

    if (result.token) {
      setToken(result.token);
    }

    return result;
  },

  async logout() {
    try {
      await apiCall('/auth/logout', { method: 'POST' });
    } finally {
      removeToken();
    }
    return { success: true };
  },

  async getCurrentUser() {
    return apiCall('/auth/me');
  },

  // User
  async updateProfile(updates) {
    return apiCall('/user/profile', {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },

  async changePassword(currentPassword, newPassword) {
    return apiCall('/user/password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword })
    });
  },

  // Projects
  async getProjects(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return apiCall(`/projects${queryParams ? '?' + queryParams : ''}`);
  },

  async getProject(id) {
    return apiCall(`/projects/${id}`);
  },

  async createProject(projectData) {
    return apiCall('/projects', {
      method: 'POST',
      body: JSON.stringify(projectData)
    });
  },

  async updateProject(id, updates) {
    return apiCall(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },

  async deleteProject(id) {
    return apiCall(`/projects/${id}`, {
      method: 'DELETE'
    });
  },

  // Dashboard
  async getDashboardStats() {
    return apiCall('/dashboard/stats');
  },

  async getRecentActivity() {
    return apiCall('/dashboard/activity');
  },

  async getUpcomingDeadlines() {
    return apiCall('/dashboard/deadlines');
  },

  async getPerformanceMetrics() {
    return apiCall('/dashboard/metrics');
  },

  // Team
  async getTeamMembers() {
    return apiCall('/team/members');
  },

  async inviteTeamMember(email, role, name) {
    return apiCall('/team/invite', {
      method: 'POST',
      body: JSON.stringify({ email, role, name })
    });
  },

  // Settings
  async getSettings() {
    return apiCall('/settings');
  },

  async updateSettings(settings) {
    return apiCall('/settings', {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  },

  // Admin
  async resetData() {
    return apiCall('/reset', { method: 'POST' });
  },

  async healthCheck() {
    return apiCall('/health');
  }
};

export default apiService;
